import * as seriesTypes from "@chartLib/lib/series";
import * as tooltipTypes from "@chartLib/lib/tooltip";
import * as indicatorTypes from "@chartLib/lib/indicator";
import * as coordinateTypes from "@chartLib/lib/coordinates";
import {
  StrokeType,
  SourcePathType,
  VolumePathType,
  ColorType,
  NumericPropertyType,
  FillType,
  BarType,
  MovingAverageType,
  WindowSizeType,
  BooleanType,
} from "../PropertyTypes";

const volumeRegistry = {
  displayName: "Volume",
  indicators: {
    // SOBV — Standard On Balance Volume (migrado do Silverlight FML.SOBV).
    // ≠ onBalanceVolume: o SOBV classifica a barra por fechamento × ABERTURA
    // (ISUP/ISDOWN do engine), não por fechamento × fechamento anterior.
    sobv: {
      displayName: "Standard On Balance Volume (SOBV)",
      key: "sobv",
      type: indicatorTypes.sobv,
      series: seriesTypes.LineSeries,
      coordinate: coordinateTypes.CurrentCoordinate,
      requireNewAxis: true,
      isPriceAxis: false,
      separatePanel: true,
      yAxisId: "SOBV",
      renderKey: "sobv",
      decimalPlaces: 0,
      defaultOptions: [],
      defaultOptionsForAppearance: [
        StrokeType({
          name: "stroke",
          displayName: "Linha",
          // Cor default do engine (1ª série da paleta por índice).
          defaultValue: {
            color: "#FFFF00",
            width: 1,
            opacity: 1,
            dash: "Solid",
          },
        }),
      ],
      tooltip: tooltipTypes.SingleValueTooltip,
      tooltipProps: {
        yLabel: "SOBV",
      },
      // Acumulado sem limites (positivo ou negativo) — auto-fit puro.
      yExtentKeys: ["sobv"],
      yExtents: (plotData) => {
        const values = plotData
          .map((d) => d.sobv)
          .filter((v) => v !== undefined && v !== null && !isNaN(v));
        if (values.length === 0) return [-1, 1];
        const min = Math.min(...values);
        const max = Math.max(...values);
        const padding = Math.max((max - min) * 0.1, 1);
        return [min - padding, max + padding];
      },
      description:
        "Standard On Balance Volume: acumula o volume das barras de alta e subtrai o das barras de baixa (fechamento comparado à abertura da própria barra), revelando pressão de compra e venda independente do preço.",
    },
    wvad: {
      displayName: "Acum/Distr William Variável (WVAD)",
      key: "wvad",
      type: indicatorTypes.wvad,
      series: seriesTypes.WVADSeries,
      coordinate: coordinateTypes.CurrentCoordinate,
      requireNewAxis: true,
      isPriceAxis: false,
      separatePanel: true,
      yAxisId: "WVAD",
      renderKey: "wvad",
      decimalPlaces: 0,
      defaultOptions: [
        NumericPropertyType({
          name: "n1",
          displayName: "Período MA1",
          min: 1,
          max: 1000,
          defaultValue: 10,
        }),
        NumericPropertyType({
          name: "n2",
          displayName: "Período MA2",
          min: 1,
          max: 1000,
          defaultValue: 20,
        }),
      ],
      defaultOptionsForAppearance: [
        // Cores default do engine (paleta por índice): WVAD=Yellow, MA1=Red, MA2=#0000B4.
        StrokeType({
          name: "strokeWvad",
          displayName: "Cor WVAD",
          defaultValue: {
            color: "#FFFF00",
            width: 1,
            opacity: 1,
            dash: "Solid",
          },
        }),
        StrokeType({
          name: "strokeMa1",
          displayName: "Cor MA1",
          defaultValue: {
            color: "#FF0000",
            width: 1,
            opacity: 1,
            dash: "Solid",
          },
        }),
        StrokeType({
          name: "strokeMa2",
          displayName: "Cor MA2",
          defaultValue: {
            color: "#0000B4",
            width: 1,
            opacity: 1,
            dash: "Solid",
          },
        }),
      ],
      labelConfig: {
        lines: [
          { key: "wvad", colorPath: "strokeWvad.color" },
          { key: "ma1", colorPath: "strokeMa1.color" },
          { key: "ma2", colorPath: "strokeMa2.color" },
        ],
      },
      tooltip: tooltipTypes.WVADTooltip,
      description:
        "Acumulação/Distribuição William Variável: (Fech−Abe)/(Máx−Mín)·Volume e duas médias móveis. Painel próprio. Não confundir com Williams A/D.",
    },
    forceIndex: {
      displayName: "Force Index (2 avgs)",
      key: "forceIndex",
      type: indicatorTypes.smoothedForceIndex,
      series: seriesTypes.ForceIndex2AvgsSeries,
      coordinate: coordinateTypes.CurrentCoordinate,
      requireNewAxis: true,
      isPriceAxis: false,
      isVolumeAxis: true,
      labelConfig: {
        lines: [
          { key: "M1", colorPath: "stroke.strokeM1" },
          { key: "M2", colorPath: "stroke.strokeM2" },
        ],
      },
      defaultOptions: [
        SourcePathType({ defaultValue: "close" }),
        VolumePathType({ defaultValue: "volume" }),
        NumericPropertyType({
          name: "m1Period",
          displayName: "Período M1 (Rápida)",
          min: 1,
          max: 100,
          defaultValue: 2,
        }),
        NumericPropertyType({
          name: "m2Period",
          displayName: "Período M2 (Lenta)",
          min: 1,
          max: 200,
          defaultValue: 13,
        }),
      ],
      defaultOptionsForAppearance: [
        StrokeType({
          name: "strokeM1",
          displayName: "Linha M1 (Rápida)",
          defaultValue: {
            color: "#FF0000",
            dash: "Solid",
            width: 1,
            opacity: 1,
          },
        }),
        StrokeType({
          name: "strokeM2",
          displayName: "Linha M2 (Lenta)",
          defaultValue: {
            color: "#00BFFF",
            dash: "Solid",
            width: 1,
            opacity: 1,
          },
        }),
        StrokeType({
          name: "strokeZeroLine",
          displayName: "Linha Zero",
          defaultValue: {
            color: "#FFFF00",
            dash: "Solid",
            width: 1,
            opacity: 0.7,
          },
        }),
      ],
      options: {
        strokeM1: { color: "#FF0000", dash: "Solid", width: 1, opacity: 1 },
        strokeM2: { color: "#00BFFF", dash: "Solid", width: 1, opacity: 1 },
        strokeZeroLine: {
          color: "#FFFF00",
          dash: "Solid",
          width: 1,
          opacity: 0.7,
        },
        sourcePath: "close",
        volumePath: "volume",
        m1Period: 2,
        m2Period: 13,
      },
      tooltip: tooltipTypes.ForceIndex2AvgsTooltip,
      renderKey: "forceIndex",
      yExtentKeys: ["forceIndex"],
      yExtents: (plotData) => {
        const values = plotData
          .map((d) => d.forceIndex)
          .filter((v) => v !== undefined && v !== null)
          .flatMap((v) => [v.m1, v.m2])
          .filter((v) => v !== undefined && v !== null && !isNaN(v));
        if (values.length === 0) return [-1, 1];
        const min = Math.min(...values);
        const max = Math.max(...values);
        const padding = Math.max((max - min) * 0.1, 1);
        return [min - padding, max + padding];
      },
      yAxisId: "ForceIndex",
      separatePanel: true,
    },
    vrsi: {
      displayName: "IFR do Volume (VRSI)",
      key: "vrsi",
      type: indicatorTypes.vrsi,
      series: seriesTypes.LineSeries,
      coordinate: coordinateTypes.CurrentCoordinate,
      requireNewAxis: true,
      isPriceAxis: false,
      defaultOptions: [
        WindowSizeType({
          name: "period",
          displayName: "Período",
          defaultValue: 6,
          min: 1,
          max: 1000,
        }),
      ],
      defaultOptionsForAppearance: [
        StrokeType({
          name: "stroke",
          displayName: "Linha",
          defaultValue: {
            color: "#8884d8",
            dash: "Solid",
            width: 1,
            opacity: 1,
          },
        }),
      ],
      tooltip: tooltipTypes.SingleValueTooltip,
      tooltipProps: {
        yLabel: "VRSI",
      },
      renderKey: "vrsi",
      // Auto-fit à faixa real dos dados, como o engine Silverlight original (o
      // Run() do VRSI não crava eixo → auto-escala). Clamp em [0,100] porque
      // VRSI = ratio ≤ 1 × 100 é matematicamente limitado — evita espaço morto.
      yExtentKeys: ["vrsi"],
      yExtents: (plotData) => {
        const values = plotData
          .map((d) => d.vrsi)
          .filter((v) => v !== undefined && v !== null && !isNaN(v));
        if (values.length === 0) return [0, 100];
        const min = Math.min(...values);
        const max = Math.max(...values);
        const padding = Math.max((max - min) * 0.1, 1);
        return [Math.max(0, min - padding), Math.min(100, max + padding)];
      },
      yAxisId: "Vrsi",
      separatePanel: true,
      decimalPlaces: 2,
      description:
        "Índice de Força Relativa do Volume — mesmos conceitos do IFR, usando volume no lugar do preço.",
    },
    vstd: {
      displayName: "Desvio Padrão do Volume (VSTD)",
      key: "vstd",
      type: indicatorTypes.vstd,
      series: seriesTypes.LineSeries,
      coordinate: coordinateTypes.CurrentCoordinate,
      requireNewAxis: true,
      isPriceAxis: false,
      isVolumeAxis: true,
      defaultOptions: [
        WindowSizeType({
          name: "period",
          displayName: "Período",
          defaultValue: 10,
          min: 1,
          max: 1000,
        }),
      ],
      defaultOptionsForAppearance: [
        StrokeType({
          name: "stroke",
          displayName: "Linha",
          defaultValue: {
            color: "#26A69A",
            dash: "Solid",
            width: 1,
            opacity: 1,
          },
        }),
      ],
      tooltip: tooltipTypes.SingleValueTooltip,
      tooltipProps: {
        yLabel: "VSTD",
      },
      renderKey: "vstd",
      // Auto-fit à faixa real dos dados, como o engine Silverlight (o Run() do
      // VSTD não crava eixo). Desvio-padrão de volume é ≥ 0 e ilimitado acima →
      // sem clamp de teto; piso em 0.
      yExtentKeys: ["vstd"],
      yExtents: (plotData) => {
        const values = plotData
          .map((d) => d.vstd)
          .filter((v) => v !== undefined && v !== null && !isNaN(v));
        if (values.length === 0) return [0, 1];
        const min = Math.min(...values);
        const max = Math.max(...values);
        const padding = Math.max((max - min) * 0.1, 1);
        return [Math.max(0, min - padding), max + padding];
      },
      yAxisId: "Vstd",
      separatePanel: true,
      decimalPlaces: 0,
      description:
        "Desvio-padrão do volume em uma janela de N períodos (amostral). Mede a dispersão/volatilidade do volume negociado.",
    },
    onBalanceVolume: {
      displayName: "On Balance Volume (OBV)",
      key: "onBalanceVolume",
      type: indicatorTypes.onBalanceVolume,
      series: seriesTypes.LineSeries,
      coordinate: coordinateTypes.CurrentCoordinate,
      requireNewAxis: true,
      isPriceAxis: false,
      isVolumeAxis: true,
      defaultOptions: [],
      defaultOptionsForAppearance: [
        StrokeType({
          name: "stroke",
          displayName: "Linha",
          defaultValue: {
            color: "#9C27B0",
            dash: "Solid",
            width: 1,
            opacity: 1,
          },
        }),
      ],
      tooltip: tooltipTypes.SingleValueTooltip,
      tooltipProps: {
        yLabel: "OBV",
      },
      renderKey: "onBalanceVolume",
      yExtentKeys: ["onBalanceVolume"],
      yExtents: (plotData) => {
        const values = plotData
          .map((d) => d.onBalanceVolume)
          .filter((v) => v != null && isFinite(v));
        if (values.length === 0) return [0, 1];
        const min = Math.min(...values);
        const max = Math.max(...values);
        const padding = (max - min) * 0.1 || 1;
        return [min - padding, max + padding];
      },
      yAxisId: "OnBalanceVolume",
      separatePanel: true,
      description:
        "Acumula volume positivo ou negativo com base no movimento do preço para medir pressão compradora ou vendedora.",
    },
    tradeVolumeIndex: {
      displayName: "Volume de Negócios (Trade Volume Index)",
      key: "tradeVolumeIndex",
      type: indicatorTypes.tradeVolumeIndex,
      series: seriesTypes.LineSeries,
      coordinate: coordinateTypes.CurrentCoordinate,
      requireNewAxis: true,
      isPriceAxis: false,
      isVolumeAxis: true,
      defaultOptions: [
        SourcePathType({ defaultValue: "close" }),
        VolumePathType({ defaultValue: "volume" }),
        NumericPropertyType({
          name: "minTickValue",
          displayName: "Valor Mínimo do Tick",
          min: 0,
          max: 100,
          step: 0.1,
          defaultValue: 0.5,
        }),
      ],
      defaultOptionsForAppearance: [
        StrokeType({
          name: "stroke",
          displayName: "Linha",
          defaultValue: {
            color: "#4CAF50",
            dash: "Solid",
            width: 1,
            opacity: 1,
          },
        }),
      ],
      tooltip: tooltipTypes.SingleValueTooltip,
      tooltipProps: {
        yLabel: "TVI",
      },
      renderKey: "tradeVolumeIndex",
      yAxisId: "TradeVolumeIndex",
      separatePanel: true,
      description:
        "Acumula volume baseado em mudanças significativas de preço. Similar ao OBV mas requer que a mudança de preço exceda um valor mínimo (minTickValue) para que o volume seja adicionado ou subtraído. Útil para filtrar ruído de mercado.",
    },
    negativeVolumeIndex: {
      displayName: "Índice de Volume Negativo (Negative Volume Index)",
      key: "negativeVolumeIndex",
      type: indicatorTypes.negativeVolumeIndex,
      series: seriesTypes.NVISeries,
      coordinate: coordinateTypes.CurrentCoordinate,
      requireNewAxis: true,
      isPriceAxis: false,
      defaultOptions: [
        WindowSizeType({
          name: "period",
          displayName: "Período MA",
          defaultValue: 255,
          min: 2,
          max: 500,
        }),
        SourcePathType({
          name: "sourcePath",
          displayName: "Campo",
          defaultValue: "close",
        }),
        VolumePathType({
          name: "volumePath",
          displayName: "Volume",
          defaultValue: "volume",
        }),
      ],
      defaultOptionsForAppearance: [
        StrokeType({
          name: "strokeNVI",
          displayName: "Linha NVI",
          defaultValue: {
            color: "#8884d8",
            dash: "Solid",
            width: 1,
            opacity: 1,
          },
        }),
        StrokeType({
          name: "strokeMA",
          displayName: "Linha MA",
          defaultValue: {
            color: "#FF0000",
            dash: "Solid",
            width: 1,
            opacity: 1,
          },
        }),
      ],
      labelConfig: {
        lines: [
          { key: "nvi", colorPath: "strokeNVI.color" },
          { key: "nviMA", colorPath: "strokeMA.color" },
        ],
      },
      tooltip: tooltipTypes.NVITooltip,
      renderKey: "negativeVolumeIndex",
      yAxisId: "NegativeVolumeIndex",
      separatePanel: true,
      decimalPlaces: 2,
      description:
        'Índice de Volume Negativo (NVI) muda apenas quando o volume diminui em relação ao período anterior, assumindo que "dinheiro inteligente" negocia em dias de baixo volume. Começa em 100 e multiplica pela variação percentual do preço apenas quando volume < volume anterior. Inclui média móvel de 255 períodos.',
    },
    positiveVolumeIndex: {
      displayName: "Índice de Volume Positivo (Positive Volume Index)",
      key: "positiveVolumeIndex",
      type: indicatorTypes.positiveVolumeIndex,
      series: seriesTypes.PVISeries,
      coordinate: coordinateTypes.CurrentCoordinate,
      requireNewAxis: true,
      isPriceAxis: false,
      defaultOptions: [
        WindowSizeType({
          name: "period",
          displayName: "Período MA",
          defaultValue: 255,
          min: 2,
          max: 500,
        }),
        SourcePathType({
          name: "sourcePath",
          displayName: "Campo",
          defaultValue: "close",
        }),
        VolumePathType({
          name: "volumePath",
          displayName: "Volume",
          defaultValue: "volume",
        }),
      ],
      defaultOptionsForAppearance: [
        StrokeType({
          name: "strokePVI",
          displayName: "Linha PVI",
          defaultValue: {
            color: "#8884d8",
            dash: "Solid",
            width: 1,
            opacity: 1,
          },
        }),
        StrokeType({
          name: "strokeMA",
          displayName: "Linha MA",
          defaultValue: {
            color: "#FF0000",
            dash: "Solid",
            width: 1,
            opacity: 1,
          },
        }),
      ],
      labelConfig: {
        lines: [
          { key: "pvi", colorPath: "strokePVI.color" },
          { key: "pviMA", colorPath: "strokeMA.color" },
        ],
      },
      tooltip: tooltipTypes.PVITooltip,
      renderKey: "positiveVolumeIndex",
      yAxisId: "PositiveVolumeIndex",
      separatePanel: true,
      decimalPlaces: 2,
      description:
        "Índice de Volume Positivo (PVI) muda apenas quando o volume aumenta em relação ao período anterior, rastreando a atividade do público em dias de alto volume. Começa em 100 e multiplica pela variação percentual do preço apenas quando volume > volume anterior. Inclui média móvel de 255 períodos.",
    },
    pvt: {
      displayName: "Price Volume Trend (PVT)",
      key: "pvt",
      type: indicatorTypes.pvt,
      series: seriesTypes.LineSeries,
      coordinate: coordinateTypes.CurrentCoordinate,
      requireNewAxis: true,
      isPriceAxis: false,
      defaultOptions: [
        SourcePathType({ defaultValue: "close" }),
        VolumePathType({ defaultValue: "volume" }),
      ],
      defaultOptionsForAppearance: [
        StrokeType({
          name: "stroke",
          displayName: "Linha",
          defaultValue: {
            color: "#00BCD4",
            dash: "Solid",
            width: 1,
            opacity: 1,
          },
        }),
      ],
      tooltip: tooltipTypes.SingleValueTooltip,
      tooltipProps: {
        yLabel: "PVT",
      },
      renderKey: "pvt",
      yExtents: (plotData) => {
        const values = plotData
          .map((d) => d.pvt)
          .filter((v) => v != null && isFinite(v));

        if (values.length === 0) return [0, 1];

        const min = Math.min(...values);
        const max = Math.max(...values);
        const padding = (max - min) * 0.1 || 1;

        return [min - padding, max + padding];
      },
      yAxisId: "PVT",
      separatePanel: true,
      description:
        "Relaciona volume e mudança percentual de preço para identificar força da tendência. Calcula acumulado de (Volume × (Preço - Preço Anterior) / Preço Anterior). Similar ao OBV mas usa percentual ao invés de direção.",
    },
    williamsad: {
      displayName: "Acumulação/Distribuição (Williams)",
      key: "williamsad",
      type: indicatorTypes.williamsad,
      series: seriesTypes.LineSeries,
      coordinate: coordinateTypes.CurrentCoordinate,
      requireNewAxis: true,
      isPriceAxis: false,
      defaultOptions: [
        BooleanType({
          name: "useVolume",
          displayName: "Usar Volume",
          defaultValue: false,
          trueLabel: "Sim",
          falseLabel: "Não",
        }),
        VolumePathType({
          name: "volumePath",
          displayName: "Tipo Volume", // Updated to clarify user is selecting volume type
          defaultValue: "volume",
        }),
      ],
      defaultOptionsForAppearance: [
        StrokeType({
          name: "stroke",
          displayName: "Linha",
          defaultValue: {
            color: "#FF6B35",
            dash: "Solid",
            width: 1,
            opacity: 1,
          },
        }),
      ],
      tooltip: tooltipTypes.SingleValueTooltip,
      tooltipProps: {
        yLabel: "W A/D",
      },
      renderKey: "williamsad",
      yExtents: (plotData) => {
        const values = plotData
          .map((d) => d.williamsad)
          .filter((v) => v != null && isFinite(v));

        if (values.length === 0) return [0, 1];

        const min = Math.min(...values);
        const max = Math.max(...values);
        const padding = (max - min) * 0.1 || 1;

        return [min - padding, max + padding];
      },
      yAxisId: "WilliamsAD",
      separatePanel: true,
      description:
        "Acumula pressão compradora/vendedora baseada na relação entre fechamento e máxima/mínima anterior. Se close > prev close: A/D = close - min(low, prev close). Se close < prev close: A/D = close - max(high, prev close). Opcionalmente multiplica por volume.",
    },
    volumeProfile: {
      displayName: "Volume Profile",
      key: "volumeProfile",
      type: undefined, // No indicator needed - series computes directly from OHLCV data
      series: seriesTypes.VolumeProfileSeries,
      coordinate: coordinateTypes.CurrentCoordinate,
      requireNewAxis: false,
      drawOnMainChart: true,
      defaultOptions: [
        NumericPropertyType({
          name: "bins",
          displayName: "Número de Faixas",
          min: 10,
          max: 100,
          defaultValue: 30,
        }),
        NumericPropertyType({
          name: "maxProfileWidthPercent",
          displayName: "Largura (%)",
          min: 5,
          max: 50,
          defaultValue: 25,
        }),
      ],
      defaultOptionsForAppearance: [
        FillType({
          name: "barsFill",
          displayName: "Cor",
          defaultValue: { color: "#b64a96", opacity: 0.3 },
        }),
      ],
      renderKey: "volumeProfile",
      description:
        "Distribuição de volume por faixa de preço. Mostra histograma horizontal indicando onde o volume foi negociado no período visível.",
    },
    vwap: {
      displayName: "VWAP (Volume Weighted Average Price)",
      key: "vwap",
      type: indicatorTypes.vwap,
      series: seriesTypes.LineSeries,
      coordinate: coordinateTypes.CurrentCoordinate,
      requireNewAxis: false,
      drawOnMainChart: true,
      defaultOptions: [],
      defaultOptionsForAppearance: [
        StrokeType({
          name: "stroke",
          displayName: "Linha",
          defaultValue: {
            color: "#2196F3",
            dash: "Solid",
            width: 1,
            opacity: 1,
          },
        }),
      ],
      tooltip: tooltipTypes.VWAPTooltip,
      tooltipProps: {
        yLabel: "VWAP",
      },
      renderKey: "vwap",
      description:
        "Preço médio ponderado pelo volume. Calculado como Σ(Preço Típico × Volume) / Σ(Volume), onde Preço Típico = (H+L+C)/3. Reinicia diariamente. Apenas válido para períodos intraday.",
    },
    klinger: {
      displayName: "Klinger Volume Oscillator",
      key: "klinger",
      type: indicatorTypes.klinger,
      series: seriesTypes.MACDSeries,
      coordinate: coordinateTypes.CurrentCoordinate,
      requireNewAxis: true,
      isPriceAxis: false,
      defaultOptions: [
        NumericPropertyType({
          name: "signalPeriods",
          displayName: "Períodos Sinal",
          min: 1,
          max: 50,
          defaultValue: 13,
        }),
        NumericPropertyType({
          name: "shortCycle",
          displayName: "Ciclo Curto",
          min: 1,
          max: 100,
          defaultValue: 34,
        }),
        NumericPropertyType({
          name: "longCycle",
          displayName: "Ciclo Longo",
          min: 1,
          max: 200,
          defaultValue: 55,
        }),
      ],
      defaultOptionsForAppearance: [
        StrokeType({
          name: "strokeMACD",
          displayName: "Cor Klinger",
          defaultValue: {
            color: "#2196F3",
            dash: "Solid",
            width: 1,
            opacity: 1,
          },
        }),
        StrokeType({
          name: "strokeSignal",
          displayName: "Cor Sinal",
          defaultValue: {
            color: "#FF9800",
            dash: "Solid",
            width: 1,
            opacity: 1,
          },
        }),
        StrokeType({
          name: "strokeZeroLine",
          displayName: "Cor Linha Zero",
          defaultValue: {
            color: "#666666",
            dash: "Solid",
            width: 1,
            opacity: 1,
          },
        }),
        BarType({
          name: "barDivergence",
          displayName: "Barra Histograma",
          defaultValue: {
            color: "#00DD00",
            fillColor: "#FF0000",
            opacity: 0.6,
            width: 1,
          },
        }),
      ],
      labelConfig: {
        lines: [
          { key: "macd", colorPath: "strokeMACD.color" },
          { key: "signal", colorPath: "strokeSignal.color" },
        ],
      },
      tooltip: tooltipTypes.KlingerTooltip,
      renderKey: "klinger",
      description:
        "Oscilador de volume que usa EMAs de volume com sinal para identificar tendências de volume de longo prazo. Calcula volume assinado (positivo/negativo) baseado na mudança de preço.",
    },
    volume: {
      displayName: "Volume",
      key: "volume",
      type: indicatorTypes.volume,
      series: seriesTypes.VolumeSeries,
      coordinate: coordinateTypes.CurrentCoordinate,
      requireNewAxis: true,
      isPriceAxis: false,
      defaultOptions: [
        VolumePathType({
          name: "volumePath",
          displayName: "Tipo Volume", // Updated to clarify user is selecting volume type
          defaultValue: "volume",
        }),
      ],
      defaultOptionsForAppearance: [
        ColorType({
          name: "upColor",
          displayName: "Cor Volume Alta",
          defaultValue: { color: "#6BA583" },
        }),
        ColorType({
          name: "downColor",
          displayName: "Cor Volume Baixa",
          defaultValue: { color: "#FF0000" },
        }),
      ],
      transformAppearanceConfig: (config) => {
        // Transform colors to function expected by VolumeSeries
        return {
          ...config,
          fill: ({ direction }) => {
            const upColor = config.upColor?.color || "#6BA583";
            const downColor = config.downColor?.color || "#FF0000";
            return direction === "up" ? upColor : downColor;
          },
        };
      },
      yExtents: (plotData, indicator) => {
        const renderKey = indicator?.renderKey || "volume";
        let max = -Infinity;

        plotData.forEach((item) => {
          if (!item) return;
          let volumeValue = null;

          // Try to get volume from indicator data structure first
          const indicatorObj = item[renderKey];
          if (
            indicatorObj &&
            typeof indicatorObj === "object" &&
            typeof indicatorObj.volume === "number"
          ) {
            volumeValue = indicatorObj.volume; // Processed indicator data: { volume: number, direction: string }
          } else if (typeof item[renderKey] === "number") {
            volumeValue = item[renderKey]; // Raw volume data: number
          }

          if (volumeValue && Number.isFinite(volumeValue)) {
            max = Math.max(max, volumeValue);
          }
        });

        return [0, max * 1.05];
      },
      tooltip: tooltipTypes.VolumeTooltip,
      tooltipProps: {
        yLabel: (config) => {
          // Dynamic label based on volumePath selection
          if (!config || !config.volumePath) return "Volume (Qtd)";

          const labelMap = {
            volume: "Volume (Qtd)",
            tickCount: "Volume (Neg)",
            financialVolume: "Volume (Fin)",
          };
          return labelMap[config.volumePath] || "Volume (Qtd)";
        },
      },
      yAxisId: "Volume",
      separatePanel: true,
      renderKey: "volume",
      description:
        "Mostra o volume de negociação com barras coloridas. Verde indica volume em alta (preço subiu), vermelho indica volume em baixa (preço desceu).",
    },
    volumeOscillator: {
      displayName: "Volume Oscillator",
      key: "volumeOscillator",
      type: indicatorTypes.volumeOscillator,
      series: seriesTypes.TRIXSeries,
      coordinate: coordinateTypes.CurrentCoordinate,
      requireNewAxis: true,
      isPriceAxis: false,
      defaultOptions: [
        NumericPropertyType({
          name: "shortCycle",
          displayName: "Ciclo Curto",
          min: 1,
          max: 100,
          defaultValue: 12,
        }),
        NumericPropertyType({
          name: "longCycle",
          displayName: "Ciclo Longo",
          min: 1,
          max: 200,
          defaultValue: 26,
        }),
        MovingAverageType({
          name: "pointsOrPercent",
          displayName: "Modo",
          options: [
            { value: "Points", label: "Pontos" },
            { value: "Percent", label: "Percentual" },
          ],
          defaultValue: "Points",
        }),
      ],
      defaultOptionsForAppearance: [
        StrokeType({
          name: "stroke",
          displayName: "Linha",
          defaultValue: {
            color: "#2196F3",
            dash: "Solid",
            width: 2,
            opacity: 1,
          },
        }),
        StrokeType({
          name: "zeroLine",
          displayName: "Linha Zero",
          defaultValue: {
            color: "#B8C2CC",
            dash: "ShortDash",
            width: 1,
            opacity: 0.7,
          },
        }),
      ],
      tooltip: tooltipTypes.VolumeOscillatorTooltip,
      renderKey: "volumeOscillator",
      yAxisId: "VolumeOscillator",
      separatePanel: true,
      yExtents: (plotData, indicator) => {
        const { TRIXSeries } = seriesTypes;
        return TRIXSeries.yExtents(plotData, undefined, plotData, indicator);
      },
      description:
        'Calcula a diferença entre duas EMAs de volume. Em modo "Pontos": EMA(Curto) - EMA(Longo). Em modo "Percentual": 100 × ((EMA(Curto) / EMA(Longo)) - 1). Linha zero indica equilíbrio entre EMAs.',
    },
    volumeRateOfChange: {
      displayName: "Taxa de variação de volume",
      key: "volumeRateOfChange",
      type: indicatorTypes.volumeRateOfChange,
      series: seriesTypes.TRIXSeries,
      coordinate: coordinateTypes.CurrentCoordinate,
      requireNewAxis: true,
      isPriceAxis: false,
      defaultOptions: [
        WindowSizeType({
          name: "period",
          displayName: "Período",
          defaultValue: 14,
          min: 1,
          max: 500,
        }),
        VolumePathType({
          name: "volumePath",
          displayName: "Tipo Volume",
          defaultValue: "volume",
        }),
      ],
      defaultOptionsForAppearance: [
        StrokeType({
          name: "stroke",
          displayName: "Linha",
          defaultValue: {
            color: "#FF6B35",
            dash: "Solid",
            width: 2,
            opacity: 1,
          },
        }),
        StrokeType({
          name: "zeroLine",
          displayName: "Linha Zero",
          defaultValue: {
            color: "#B8C2CC",
            dash: "ShortDash",
            width: 1,
            opacity: 0.7,
          },
        }),
      ],
      tooltip: tooltipTypes.VolumeROCTooltip,
      renderKey: "volumeRateOfChange",
      yAxisId: "VolumeROC",
      separatePanel: true,
      yExtents: (plotData, indicator) => {
        const { TRIXSeries } = seriesTypes;
        return TRIXSeries.yExtents(plotData, undefined, plotData, indicator);
      },
      description:
        "Taxa de variação percentual do volume. Calcula: 100 × ((Volume atual / Volume N períodos atrás) - 1). Valores positivos indicam aumento de volume, negativos indicam diminuição. Linha zero indica volume igual ao período de referência.",
    },
  },
};

export default volumeRegistry;
